import React from "react";
import { Form, message, Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";

// common
import {
  NameOnCard,
  CardElementsStyling,
  CustomCardElement,
} from "common/credit_card";
import ErrorAlerting from "common/error_alerting";
import Button from "common/button";
import SET_PM_AS_DEFAULT from "graphql/mutations/SetPmAsDefault";
import GET_PAYMENT_METHODS from "graphql/queries/GetPaymentMethods";

// styling

// hooks
import useStripePaymentMethod from "hooks/useStripePaymentMethod";
import useErrors from "hooks/useErrors";
import { PaymentMethod } from "types";
import { CustomSection, SectionHeader, SectionText } from "./MyBillingsStyling";

const AddPaymentMethodForm = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation("my_billing");
  const { createPaymentMethod, loading } = useStripePaymentMethod();
  const { errors, handleError, closeError } = useErrors();
  const [setPmAsDefault] = useMutation(SET_PM_AS_DEFAULT, {
    refetchQueries: [
      {
        query: GET_PAYMENT_METHODS,
      },
    ],
  });

  const handleSetPmAsDefault = async (
    paymentMethod: PaymentMethod | undefined
  ) => {
    if (paymentMethod) {
      const adInfo = localStorage.getItem("adInfo") || null;
      await setPmAsDefault({
        variables: {
          input: {
            attributes: {
              paymentMethodId: paymentMethod.id,
              adInfo,
            },
          },
        },
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const paymentMethod = await createPaymentMethod();
      await handleSetPmAsDefault(paymentMethod);

      // SUCCESS
      message.success(t("New payment method added successfully"));

      // Reset the form
      form.resetFields();
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <CustomSection style={{ marginBottom: "20px" }}>
      <SectionHeader>{t("ADD A NEW PAYMENT METHOD")}</SectionHeader>
      <SectionText>
        {t(
          "To add a new payment method: A test charge will be processed on your bank account. It will be immediately returned once the payment method is successfully added."
        )}
      </SectionText>
      <Form name="newPaymentMethod" form={form} onFinish={handleSubmit}>
        <ErrorAlerting errors={errors} onClose={closeError} />
        <Row>
          <Col xs={24} sm={20} md={16}>
            <Form.Item
              name="cardInfo"
              rules={[
                {
                  required: true,
                  message: t("Card info is required."),
                },
                () => ({
                  validator(rule, value) {
                    if (value && !value.complete) {
                      return Promise.reject(new Error(t("Invalid card info.")));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <CustomCardElement options={CardElementsStyling} />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col xs={24} sm={20} md={16}>
            <Form.Item
              name="cardholderName"
              rules={[
                {
                  required: true,
                  message: t("Card holder's name is required."),
                },
              ]}
            >
              <NameOnCard placeholder={t("Name on Card")} />
            </Form.Item>
          </Col>
        </Row>

        <Button htmlType="submit" loading={loading}>
          {t("ADD PAYMENT METHOD")}
        </Button>
      </Form>
    </CustomSection>
  );
};

export default AddPaymentMethodForm;
